import * as THREE from 'three';
import { TrailEffect } from '../effects/TrailEffect';
import { EngineGlow } from '../effects/EngineGlow';

export type ShipClass = 'micro' | 'standard' | 'priority' | 'whale';

export interface TransactionData {
  amount: number;
  fee: number;
  feeRate: number;
  size: number;
}

export interface TrajectoryData {
  origin: THREE.Vector3;
  destination: THREE.Vector3;
  currentPosition: THREE.Vector3;
  progress: number;
  path: THREE.CatmullRomCurve3;
  // Phase 2: Mempool entry and orbital movement
  phase: 'broadcast' | 'approaching-mempool' | 'entering-mempool' | 'orbiting-mempool' | 'selected';
  mempoolLayerPosition?: THREE.Vector3;
  orbitalParams?: {
    radius: number;
    angularSpeed: number;
    inclination: number;
    currentAngle: number;
  };
  decelerationFactor: number;
}

export interface ActivityData {
  dockingShips: Set<string>;
  surfaceGlow: number;
  activityRings: any[];
}

export class TransactionShip {
  public readonly id: string;
  public readonly mesh: THREE.Mesh;
  public shipClass: ShipClass;
  public baseSpeed: number;
  public color: THREE.Color;
  public glowIntensity: number;
  public trajectory: TrajectoryData;
  public data: TransactionData;
  public trail: TrailEffect;
  public engineGlow: EngineGlow;
  public isActive: boolean = false;

  constructor(id: string, mesh: THREE.Mesh) {
    this.id = id;
    this.mesh = mesh;
    this.shipClass = 'standard';
    this.baseSpeed = 1.0;
    this.color = new THREE.Color(0x44ff88);
    this.glowIntensity = 1.0;
    
    this.trajectory = {
      origin: new THREE.Vector3(),
      destination: new THREE.Vector3(),
      currentPosition: new THREE.Vector3(),
      progress: 0,
      path: new THREE.CatmullRomCurve3([new THREE.Vector3(), new THREE.Vector3()]),
      // Phase 2: Initialize new trajectory properties
      phase: 'broadcast',
      mempoolLayerPosition: undefined,
      orbitalParams: undefined,
      decelerationFactor: 1.0
    };
    
    this.data = {
      amount: 0,
      fee: 0,
      feeRate: 0,
      size: 0
    };
    
    this.trail = new TrailEffect(20);
    this.engineGlow = new EngineGlow(this.mesh);
  }

  public activate(transactionData: TransactionData, shipClass: ShipClass): void {
    this.isActive = true;
    this.data = transactionData;
    this.shipClass = shipClass;
    this.updateVisualProperties();
    this.mesh.visible = true;
    this.trail.clear();
    this.engineGlow.setIntensity(this.glowIntensity);
  }

  public deactivate(): void {
    this.isActive = false;
    this.mesh.visible = false;
    this.trail.clear();
    this.engineGlow.setIntensity(0);
    this.trajectory.progress = 0;
  }

  public updatePosition(deltaTime: number, timeScale: number = 1.0): void {
    if (!this.isActive) return;

    // Phase 2: Handle different movement phases
    switch (this.trajectory.phase) {
      case 'broadcast':
      case 'approaching-mempool':
        this.updateBroadcastMovement(deltaTime, timeScale);
        break;
      case 'entering-mempool':
        this.updateMempoolEntry(deltaTime, timeScale);
        break;
      case 'orbiting-mempool':
        this.updateOrbitalMovement(deltaTime, timeScale);
        break;
      case 'selected':
        this.updateSelectedMovement(deltaTime, timeScale);
        break;
    }
  }

  private updateBroadcastMovement(deltaTime: number, timeScale: number): void {
    if (this.trajectory.progress >= 1.0) return;

    // Validate trajectory path exists and has valid points
    if (!this.trajectory.path || !this.trajectory.path.points || this.trajectory.path.points.length < 2) {
      if (!this.mesh.userData['pathErrorLogged']) {
        console.warn(`TransactionShip ${this.id}: INVALID TRAJECTORY PATH - deactivating ship`);
        this.mesh.userData['pathErrorLogged'] = true;
      }
      this.deactivate();
      return;
    }

    const pathLength = this.trajectory.path.getLength();
    if (pathLength <= 0) {
      if (!this.mesh.userData['lengthErrorLogged']) {
        console.error(`TransactionShip ${this.id}: PATH LENGTH IS INVALID (${pathLength})`);
        this.mesh.userData['lengthErrorLogged'] = true;
      }
      this.deactivate();
      return;
    }

    const speed = this.baseSpeed * timeScale * this.trajectory.decelerationFactor;
    const increment = (speed * deltaTime) / pathLength;
    
    this.trajectory.progress = Math.min(1.0, this.trajectory.progress + increment);
    
    try {
      const point = this.trajectory.path.getPointAt(this.trajectory.progress);
      
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number' || typeof point.z !== 'number') {
        if (!this.mesh.userData['pointErrorLogged']) {
          console.warn(`TransactionShip ${this.id}: INVALID POINT from path, deactivating ship`);
          this.mesh.userData['pointErrorLogged'] = true;
        }
        this.deactivate();
        return;
      }
      
      this.trajectory.currentPosition.copy(point);
      this.mesh.position.copy(point);
      
      // Orient ship along path
      if (this.trajectory.progress < 0.99) {
        try {
          const tangent = this.trajectory.path.getTangentAt(this.trajectory.progress);
          if (tangent && typeof tangent.x === 'number') {
            this.mesh.lookAt(point.clone().add(tangent));
          }
        } catch (tangentError) {
          // Continue without orientation
        }
      }
      
      // Update trail
      this.trail.addPoint(point.clone());
      
      // Phase 2: Check if approaching mempool boundary
      if (this.trajectory.phase === 'broadcast') {
        // This will be set by DynamicObjectsManager when mempool is detected
        this.trajectory.phase = 'approaching-mempool';
      }
    } catch (error) {
      if (!this.mesh.userData['generalErrorLogged']) {
        console.error(`TransactionShip ${this.id}: ERROR updating position:`, error);
        this.mesh.userData['generalErrorLogged'] = true;
      }
      this.deactivate();
    }
  }

  private updateMempoolEntry(deltaTime: number, timeScale: number): void {
    // Phase 2: Gradual deceleration as ship enters mempool
    // Continue on trajectory path but with decreasing speed
    this.trajectory.decelerationFactor = Math.max(0.1, this.trajectory.decelerationFactor - deltaTime * 0.5);
    
    // Continue broadcast movement with deceleration
    this.updateBroadcastMovement(deltaTime, timeScale);
    
    // Check if ship has reached its fee layer position
    if (this.trajectory.mempoolLayerPosition && 
        this.trajectory.currentPosition.distanceTo(this.trajectory.mempoolLayerPosition) < 2.0) {
      // Transition to orbital movement
      this.trajectory.phase = 'orbiting-mempool';
      this.trajectory.progress = 0; // Reset for orbital phase
    }
  }

  private updateOrbitalMovement(deltaTime: number, timeScale: number): void {
    // Phase 2: Orbital movement within mempool layer
    if (!this.trajectory.orbitalParams || !this.trajectory.mempoolLayerPosition) return;
    
    const params = this.trajectory.orbitalParams;
    
    // Update orbital angle
    params.currentAngle += params.angularSpeed * deltaTime * timeScale;
    if (params.currentAngle > Math.PI * 2) {
      params.currentAngle -= Math.PI * 2;
    }
    
    // Calculate new position in orbit
    const center = this.trajectory.mempoolLayerPosition;
    const x = center.x + Math.cos(params.currentAngle) * params.radius;
    const z = center.z + Math.sin(params.currentAngle) * params.radius;
    const y = center.y + Math.sin(params.currentAngle + params.inclination) * 2;
    
    const newPosition = new THREE.Vector3(x, y, z);
    this.trajectory.currentPosition.copy(newPosition);
    this.mesh.position.copy(newPosition);
    
    // Orient ship tangentially to orbit
    const tangent = new THREE.Vector3(-Math.sin(params.currentAngle), 0, Math.cos(params.currentAngle));
    this.mesh.lookAt(newPosition.clone().add(tangent));
    
    // Update trail
    this.trail.addPoint(newPosition.clone());
  }

  private updateSelectedMovement(deltaTime: number, _timeScale: number): void {
    // Phase 3: Ship has been selected by miner and is being extracted by tractor beam
    // Mining collector handles the position updates via applyExtractionForce
    // Ship just needs to maintain visual effects for extraction state
    
    // Increase engine glow intensity during extraction
    this.glowIntensity = Math.min(2.0, this.glowIntensity + deltaTime * 2.0);
    
    // Apply visual effects for being selected/extracted
    this.applyPhaseVisualEffects();
  }

  public update(deltaTime: number): void {
    if (!this.isActive) return;
    
    this.trail.update(deltaTime);
    this.engineGlow.update(deltaTime);
  }

  public setTrajectory(origin: THREE.Vector3, destination: THREE.Vector3, path: THREE.CatmullRomCurve3): void {
    // Validate inputs
    if (!origin || !destination || !path) {
      console.warn(`TransactionShip ${this.id}: Invalid trajectory parameters provided`);
      return;
    }

    // Reset error logging flags when setting new trajectory
    this.mesh.userData['pathErrorLogged'] = false;
    this.mesh.userData['lengthErrorLogged'] = false;
    this.mesh.userData['pointErrorLogged'] = false;
    this.mesh.userData['tangentErrorLogged'] = false;
    this.mesh.userData['generalErrorLogged'] = false;

    // Validate origin and destination are not at (0,0,0) and are not identical
    const isOriginZero = (origin.x === 0 && origin.y === 0 && origin.z === 0);
    const isDestinationZero = (destination.x === 0 && destination.y === 0 && destination.z === 0);
    const distance = origin.distanceTo(destination);
    
    if (isOriginZero || isDestinationZero || distance < 0.1) {
      console.warn(`TransactionShip ${this.id}: Invalid positions - creating safe fallback trajectory`);
      
      // Create a safe fallback trajectory
      const safeOrigin = isOriginZero ? new THREE.Vector3(50, 0, 0) : origin.clone();
      const safeDestination = isDestinationZero ? new THREE.Vector3(30, 0, 0) : destination.clone();
      
      // Ensure minimum distance between points
      if (safeOrigin.distanceTo(safeDestination) < 2) {
        safeDestination.copy(safeOrigin).add(new THREE.Vector3(5, 0, 0));
      }
      
      const safePath = new THREE.CatmullRomCurve3([safeOrigin, safeDestination]);
      this.setValidTrajectory(safeOrigin, safeDestination, safePath);
      return;
    }

    // Validate path has sufficient points
    if (!path.points || path.points.length < 2) {
      console.warn(`TransactionShip ${this.id}: Path has insufficient control points, creating fallback path`);
      const fallbackPath = new THREE.CatmullRomCurve3([origin.clone(), destination.clone()]);
      this.setValidTrajectory(origin, destination, fallbackPath);
      return;
    }

    // Validate the path length before setting
    const pathLength = path.getLength();
    if (pathLength <= 0) {
      console.warn(`TransactionShip ${this.id}: Generated path has invalid length (${pathLength}), using fallback`);
      const direction = new THREE.Vector3().subVectors(destination, origin);
      if (direction.length() < 0.1) {
        direction.set(1, 0, 0); // Default direction if positions are identical
      }
      direction.normalize().multiplyScalar(Math.max(2, distance));
      const validDestination = origin.clone().add(direction);
      const fallbackPath = new THREE.CatmullRomCurve3([origin.clone(), validDestination]);
      this.setValidTrajectory(origin, validDestination, fallbackPath);
      return;
    }

    this.setValidTrajectory(origin, destination, path);
  }

  private setValidTrajectory(origin: THREE.Vector3, destination: THREE.Vector3, path: THREE.CatmullRomCurve3): void {
    this.trajectory.origin.copy(origin);
    this.trajectory.destination.copy(destination);
    this.trajectory.path = path;
    this.trajectory.progress = 0;
    this.trajectory.currentPosition.copy(origin);
    
    // Phase 2: Initialize new trajectory properties
    this.trajectory.phase = 'broadcast';
    this.trajectory.mempoolLayerPosition = undefined;
    this.trajectory.orbitalParams = undefined;
    this.trajectory.decelerationFactor = 1.0; // Start at full speed
    
    this.mesh.position.copy(origin);
  }

  /**
   * Phase 2: Transition ship to mempool entry phase with fee-based positioning
   */
  public enterMempool(mempoolNebula: any, feeRate: number): void {
    if (this.trajectory.phase !== 'approaching-mempool') return;
    
    this.trajectory.phase = 'entering-mempool';
    this.trajectory.mempoolLayerPosition = mempoolNebula.getFeeLayerPosition(feeRate, this.id);
    this.trajectory.orbitalParams = {
      ...mempoolNebula.getOrbitalParameters(this.trajectory.mempoolLayerPosition, feeRate),
      currentAngle: Math.random() * Math.PI * 2
    };
    
    // Start deceleration
    this.trajectory.decelerationFactor = 0.8;
  }

  /**
   * Phase 2: Check if ship is ready to enter mempool
   */
  public isApproachingMempool(mempoolPosition: THREE.Vector3, boundaryRadius: number): boolean {
    const distance = this.trajectory.currentPosition.distanceTo(mempoolPosition);
    return distance <= boundaryRadius + 10 && this.trajectory.phase === 'broadcast';
  }

  private updateVisualProperties(): void {
    const properties = this.getShipProperties();
    this.mesh.scale.setScalar(properties.scale);
    this.baseSpeed = properties.speed;
    this.color.setHex(properties.color);
    this.glowIntensity = properties.glowIntensity;
    this.trail.setMaxPoints(properties.trailLength);
    this.trail.setColor(this.color);
    
    // Phase 2: Apply visual changes based on ship phase
    this.applyPhaseVisualEffects();
    
    // Update material
    if (this.mesh.material instanceof THREE.MeshStandardMaterial) {
      this.mesh.material.color = this.color;
      this.mesh.material.emissive = this.color;
      this.mesh.material.emissiveIntensity = this.getEmissiveIntensity();
    }
  }

  /**
   * Phase 2: Apply visual effects based on current trajectory phase
   */
  private applyPhaseVisualEffects(): void {
    let emissiveMultiplier = 0.5; // Default
    let scaleMultiplier = 1.0;
    
    switch (this.trajectory.phase) {
      case 'broadcast':
        // Normal appearance
        emissiveMultiplier = 0.5;
        break;
        
      case 'approaching-mempool':
        // Slightly brighter as ship approaches mempool
        emissiveMultiplier = 0.6;
        break;
        
      case 'entering-mempool':
        // Pulsing effect as ship decelerates and enters
        const pulseSpeed = 3.0;
        const pulseFactor = 0.5 + 0.3 * Math.sin(Date.now() * 0.01 * pulseSpeed);
        emissiveMultiplier = 0.4 + pulseFactor;
        scaleMultiplier = 0.9 + pulseFactor * 0.1; // Slight size pulsing
        break;
        
      case 'orbiting-mempool':
        // Steady glow while orbiting in mempool
        emissiveMultiplier = 0.7;
        // Add subtle orbital trail enhancement
        // Add subtle orbital trail enhancement (maxPoints is private, so we'll use a reasonable default)
        this.trail.setMaxPoints(30);
        break;
        
      case 'selected':
        // Bright, attention-grabbing appearance when selected
        emissiveMultiplier = 1.0;
        scaleMultiplier = 1.1;
        break;
    }
    
    // Apply scale changes
    if (scaleMultiplier !== 1.0) {
      const currentScale = this.mesh.scale.x;
      this.mesh.scale.setScalar(currentScale * scaleMultiplier);
    }
    
    // Store emissive multiplier for use in material update
    this.mesh.userData['emissiveMultiplier'] = emissiveMultiplier;
  }

  /**
   * Phase 2: Get emissive intensity with phase-based modifications
   */
  private getEmissiveIntensity(): number {
    const baseIntensity = 0.5;
    const multiplier = this.mesh.userData['emissiveMultiplier'] || 1.0;
    return baseIntensity * multiplier;
  }

  private getShipProperties() {
    const properties = {
      micro: {
        scale: 0.5,
        speed: 0.5,
        color: 0x4488ff,
        trailLength: 10,
        glowIntensity: 0.5
      },
      standard: {
        scale: 1.0,
        speed: 1.0,
        color: 0x44ff88,
        trailLength: 20,
        glowIntensity: 1.0
      },
      priority: {
        scale: 1.5,
        speed: 2.0,
        color: 0xffaa44,
        trailLength: 30,
        glowIntensity: 1.5
      },
      whale: {
        scale: 3.0,
        speed: 3.0,
        color: 0xff4444,
        trailLength: 50,
        glowIntensity: 2.0
      }
    };
    
    return properties[this.shipClass];
  }

  public dispose(): void {
    this.trail.dispose();
    this.engineGlow.dispose();
    if (this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }
    if (this.mesh.material) {
      if (Array.isArray(this.mesh.material)) {
        this.mesh.material.forEach(mat => mat.dispose());
      } else {
        this.mesh.material.dispose();
      }
    }
  }
}